import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Typography,
} from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { format } from "date-fns";
import {
  LoaderFunctionArgs,
  useLoaderData,
  useRevalidator,
} from "react-router-dom";
import { DynamicTable, TableColumn } from "../../components/common/table/Table";
import { StringPeelForm } from "../../components/common/form/StringPeelForm";
import { fetchLastStringPeel } from "../../services/stringPeelService";
import { StringPeelType } from "../../types/stringPeelTypes";

export async function loader(_: LoaderFunctionArgs) {
  const stationId = 11;
  const rows = await fetchLastStringPeel();
  return { title: "String Peel Test", stationId, rows };
}

export default function StringPeelPage() {
  const methods = useForm();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loaderData = useLoaderData() as {
    stationId: number;
    rows: StringPeelType[];
  };
  const revalidator = useRevalidator();
  const isReloading = revalidator.state === "loading";

  const handleSuccess = () => {
    setShowSuccess(true);
    revalidator.revalidate();
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    setShowSnackbar(true);
  };

  const handleSnackbarClose = (
    _: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setShowSnackbar(false);
  };

  return (
    <Box p={4}>
      <Box display="flex" flexDirection="row" gap={4} alignItems="flex-start">
        {/* Form Side */}
        <Box flex={1}>
          <FormProvider {...methods}>
            <StringPeelForm
              stationId={loaderData.stationId}
              onSuccess={handleSuccess}
              onError={handleError}
            />
          </FormProvider>
        </Box>

        {/* Table Side */}
        <Box flex={3}>
          {isReloading ? (
            <CircularProgress />
          ) : (
            <DynamicTable columns={columns} data={loaderData.rows} />
          )}
        </Box>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          String Peel erfolgreich gespeichert!
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error || "Ein Fehler ist aufgetreten"}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const FRONT_MIN = 80;
const BACK_MIN = 90;

const columns: TableColumn<StringPeelType>[] = [
  {
    key: "dateInProduction",
    label: "Produktionsdatum",
    render: (value) =>
      value ? format(new Date(value as string), "yyyy-MM-dd HH:mm:ss") : "-",
  },
  {
    key: "username",
    label: "Operator",
    render: (value) => value || "-",
  },
  {
    key: "manufacturingLine",
    label: "Linie",
    render: (value) => value || "-",
  },
  {
    key: "adhesiveForceFrontMin",
    label: "Haftkraft VS [N]",
    render: (_value, row) =>
      `${row.adhesiveForceFrontMin ?? "-"}-${row.adhesiveForceFrontMax ?? "-"}`,
  },
  {
    key: "adhesiveForceBackMin",
    label: "Haftkraft HS [N]",
    render: (_value, row) =>
      `${row.adhesiveForceBackMin ?? "-"}-${row.adhesiveForceBackMax ?? "-"}`,
  },
  {
    key: "lubricationFront",
    label: "Benetzung VS [%]",
    render: (value) => {
      if (value == null) return "-";
      const v = typeof value === "number" ? value : parseFloat(String(value));
      if (isNaN(v)) return String(value);
      const low = v < FRONT_MIN;
      return (
        <Typography component="span" color={low ? "error.main" : "inherit"}>
          {v}
        </Typography>
      );
    },
  },
  {
    key: "lubricationBack",
    label: "Benetzung HS [%]",
    render: (value) => {
      if (value == null) return "-";
      const v = typeof value === "number" ? value : parseFloat(String(value));
      if (isNaN(v)) return String(value);
      const low = v < BACK_MIN;
      return (
        <Typography component="span" color={low ? "error.main" : "inherit"}>
          {v}
        </Typography>
      );
    },
  },
  {
    key: "isSiliciumBreakoutsFront",
    label: "Si-Ausbrüche VS",
    render: (value) => (value ? "Ja" : "Nein"),
  },
  {
    key: "isSiliciumBreakoutsBack",
    label: "Si-Ausbrüche HS",
    render: (value) => (value ? "Ja" : "Nein"),
  },
  {
    key: "isGood",
    label: "Gesamt i.O.",
    render: (value) => (value ? "Ja" : "Nein"),
  },
  {
    key: "notes",
    label: "Bemerkungen",
    render: (value) => value || "-",
  },
];
